import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authResolved, setAuthResolved] = useState(false);

    const fetchUser = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/auth/me');
            setUser(data);
            return data;
        } catch (err) {
            setUser(null);
            throw err;
        }
    }, []);

    const login = useCallback(async (email, password) => {
        await axiosInstance.post('/auth/login', { email, password });
        const userData = await fetchUser();
        return userData;
    }, [fetchUser]);

    const logout = useCallback(async () => {
        try {
            await axiosInstance.post('/auth/logout');
        } catch (err) {
            // Ignorar error de red; limpiar sesión igual
        } finally {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const bootstrap = async () => {
            try {
                await fetchUser();
            } catch (err) {
                if (isMounted) setUser(null);
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setAuthResolved(true);
                }
            }
        };

        bootstrap();
        return () => { isMounted = false; };
    }, [fetchUser]);

    const value = {
        user,
        loading,
        authResolved,
        isAuthenticated: !!user,
        login,
        logout,
        fetchUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return ctx;
}
